import { api, TCustomAxiosPaginationResponse, TCustomAxiosResponse } from '..';
import { MIN_CHARS_TO_DO_SEARCH } from '@/constants/numberVars';
import { label4BEMapper } from '@/utils/mappers/label';
import { APPOINTMENT_TYPES_DISPLAY_LABELS, DESIGNATIONS_DISPLAY_LABELS } from '@/constants/stringVars';
import { TUserLocation } from '@/types/User.types';
import { TProviderApi, TProviderFilterOption, TProvider } from '@/types/Provider.types';
import { apiPaths } from '@/services/api/paths';
import { SUPER_ADMIN_PAGE_SIZE } from '@/constants/numberVars';

export const getProviders = async (
  page: number,
  filterOption: TProviderFilterOption,
  filterText: string,
  status: 'active' | 'inactive',
): Promise<TCustomAxiosPaginationResponse<TProviderApi[]>> => {
  const params: { [key: string]: string | number } = {
    limit: SUPER_ADMIN_PAGE_SIZE,
    page,
  };

  if (filterText && filterText?.length >= MIN_CHARS_TO_DO_SEARCH) {
    const filterOptionBE = label4BEMapper(filterOption);
    const displayLabels =
      filterOption === 'Appointment Type' ? APPOINTMENT_TYPES_DISPLAY_LABELS : DESIGNATIONS_DISPLAY_LABELS;

    const filterTextBE = Object.entries(displayLabels).find((entry) => entry[1] === filterText)![0];
    params[filterOptionBE] = filterTextBE;
  }

  const isActive = status === 'active' ? true : false;
  const endpoint = isActive ? apiPaths.providers.root : apiPaths.providers.inactive;

  const { data } = await api.get<TCustomAxiosPaginationResponse<TProviderApi[]>>(endpoint, {
    params,
  });

  return data;
};

export const postProvider = async ({ form, photo }: { form: TProvider; photo: File | null }) => {
  const formData = new FormData();

  Object.entries(form).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        if (typeof item === 'string') {
          formData.append(`${key}[]`, item.toString());
        } else {
          Object.entries(item).forEach(([itemKey, itemValue]) =>
            formData.append(`${key}[${i}][${itemKey}]`, itemValue),
          );
        }
      });
    } else {
      if (value) {
        formData.append(key, typeof value === 'boolean' ? value.toString() : value);
      }
    }
  });

  if (photo) {
    formData.append('profilePhoto', new File([photo], photo.name));
  }

  const response = await api.post(apiPaths.providers.root, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const getProviderById = async (id: string): Promise<TProviderApi> => {
  const { data } = await api.get(apiPaths.providers.byId(id));

  const provider = data.data as TProviderApi;

  return provider;
};

export const patchProvider = async ({ form, photo, id }: { form: TProviderApi; photo: File | null; id: string }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { profile_photo, _id, created_at, updated_at, ...restForm } = form;

  const formData = new FormData();

  Object.entries(restForm).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        if (typeof item === 'string') {
          formData.append(`${key}[]`, item.toString());
        } else {
          Object.entries(item).forEach(([itemKey, itemValue]) =>
            formData.append(`${key}[${i}][${itemKey}]`, itemValue),
          );
        }
      });
    } else {
      if (value) {
        formData.append(key, typeof value === 'boolean' ? value.toString() : value);
      }
    }
  });

  if (photo) {
    formData.append('profilePhoto', new File([photo], photo.name));
  }

  const response = await api.patch(apiPaths.providers.byId(id), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const patchChangeProviderStatus = async ({ id, isActive }: { id: string; isActive: boolean }) => {
  const response = await api.patch(apiPaths.providers.byId(id), { isActive });
  return response.data;
};

export const updateProviderLocationPatch = async (
  providerId: string,
  locations: TUserLocation[],
): Promise<TProviderApi> => {
  const { data } = await api.patch<TCustomAxiosResponse<TProviderApi>>(apiPaths.providers.locations(providerId), {
    locations,
  });

  return data.data;
};
